<script setup lang="ts">
import { ref, computed, onUnmounted, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import Select from 'primevue/select'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import FloatLabel from 'primevue/floatlabel'
import { AVAILABLE_GMGN_CHAINS } from '@/utils/consts'
import { usePoolsData, type ComputedPool } from '@/composable/usePoolsData'
import PoolPairCard from '@/components/pools/PoolPairCard.vue'
import { Card } from 'primevue'

const route = useRoute()
const chain = ref('SOL')
const contract = ref('')
const amountStr = ref('100')
const slippageStr = ref('1')

onMounted(() => {
  const routeChain = route.params.chain?.toString()
  const routeContract = route.params.contract?.toString()
  if (routeChain) {
    chain.value = routeChain.toUpperCase()
  }
  if (routeContract) {
    contract.value = routeContract
    loadPools()
  }
})
const amount = computed(() => Number(amountStr.value) || 100)
const slippage = computed(() => Number(slippageStr.value) || 1)

const { pools, loading, loadPools, stopPools } = usePoolsData(
  chain,
  contract,
  amount,
)

const onLoad = () => {
  if (!chain.value || !contract.value) return
  loadPools()
}

onUnmounted(() => {
  stopPools()
})

watch(() => route.params, (params) => {
  const newChain = params.chain?.toString()
  const newContract = params.contract?.toString()
  if (newChain) {
    chain.value = newChain.toUpperCase()
  }
  if (newContract) {
    contract.value = newContract
    loadPools()
  }
}, { deep: true })

watch([chain, contract], () => {
  stopPools()
})

const poolPairs = computed(() => {
  const list = pools.value
  const pairs: Array<{
    buyPool: ComputedPool
    sellPool: ComputedPool
    spread: number | null
  }> = []

  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const a = list[i]!
      const b = list[j]!

      const spreadAB = a.buyPrice && b.sellPrice && a.buyPrice > 0
        ? ((b.sellPrice - a.buyPrice) / a.buyPrice) * 100
        : null
      const spreadBA = b.buyPrice && a.sellPrice && b.buyPrice > 0
        ? ((a.sellPrice - b.buyPrice) / b.buyPrice) * 100
        : null

      if (spreadAB !== null && spreadBA !== null) {
        if (spreadAB >= spreadBA) {
          pairs.push({ buyPool: a, sellPool: b, spread: spreadAB })
        } else {
          pairs.push({ buyPool: b, sellPool: a, spread: spreadBA })
        }
      } else if (spreadAB !== null) {
        pairs.push({ buyPool: a, sellPool: b, spread: spreadAB })
      } else if (spreadBA !== null) {
        pairs.push({ buyPool: b, sellPool: a, spread: spreadBA })
      }
    }
  }

  return pairs.sort((x, y) => (y.spread ?? -Infinity) - (x.spread ?? -Infinity))
})
</script>

<template>
  <div>
    <Card>
      <template #subtitle>
        <span class="pit success">{{ $t("openNewTab") }}</span>
      </template>
      <template #content>
        <div class="flex flex-wrap items-end gap-3 p-2">
          <div class="flex-1 min-w-[200px]">
            <FloatLabel variant="on">
              <Select v-model="chain" :options="AVAILABLE_GMGN_CHAINS" fluid />
              <label>{{ $t('chain') }}</label>
            </FloatLabel>
          </div>
          <div class="flex-[2] min-w-[200px]">
            <FloatLabel variant="on">
              <InputText v-model="contract" fluid :placeholder="$t('contractAddress')" />
              <label>{{ $t('contractAddress') }}</label>
            </FloatLabel>
          </div>
          <div class="w-28">
            <FloatLabel variant="on">
              <InputText v-model="amountStr" type="number" fluid />
              <label>Объём (USDT)</label>
            </FloatLabel>
          </div>
          <div class="w-28">
            <FloatLabel variant="on">
              <InputText v-model="slippageStr" type="number" fluid />
              <label>Slippage (%)</label>
            </FloatLabel>
          </div>
          <Button :loading="loading" @click="onLoad" severity="secondary" class="mb-0.5">
            {{ $t('next') }}
          </Button>
        </div>
      </template>
    </Card>

    <div v-if="poolPairs.length > 0">
      <div class="flex items-center justify-between mb-3">
        <span v-if="loading" class="text-sm text-muted-color flex items-center gap-2">
          <ProgressSpinner style="width: 16px; height: 16px" strokeWidth="4" />
          Обновление...
        </span>
      </div>

      <div class="grid grid-cols-1 gap-3">
        <PoolPairCard v-for="(pair, idx) in poolPairs" :key="`${pair.buyPool.address}-${pair.sellPool.address}-${idx}`"
          :buy-pool="pair.buyPool" :sell-pool="pair.sellPool" :spread="pair.spread" :chain="chain.toLowerCase()"
          :amount="amount" :slippage="slippage" />
      </div>
    </div>

    <div v-else-if="pools.length > 0 && poolPairs.length === 0" class="text-center text-muted-color py-20">
      {{ $t('noDataError') }}
    </div>
  </div>
</template>
